import {useEffect, useState} from "react";
import {Link, Navigate} from "react-router-dom";
import {useData} from "../../hooks/useData";
import {doFetch} from "../../util/fetch";
import {AdminAuthResponse, AdminServer, AdminUser, GetUsersResponse} from "./interfaces";
import UsersTable from "./UsersTable";
import ServersTable from "./ServersTable";
import SvgLoader from "../../svg/SvgLoader";

export default function AdminDashboard() {
    // The admin dashboard is intentionally separate from the /client app. The auth endpoint is
    // anonymous and reports isAdmin; the real access control lives on each backend admin endpoint.
    const auth = useData<AdminAuthResponse>("/api/client/auth")

    if (auth.isLoading) {
        return <LoadingScreen/>
    }

    if (!auth.data?.isLoggedIn) {
        return <Navigate to="/login" replace/>
    }

    if (!auth.data.isAdmin) {
        return <Forbidden/>
    }

    return <Dashboard userName={auth.data.userName} avatarUrl={auth.data.avatarUrl}/>
}

function Dashboard(props: { userName?: string, avatarUrl?: string }) {
    const [users, setUsers] = useState<AdminUser[]>()
    const [servers, setServers] = useState<AdminServer[]>()
    const [error, setError] = useState<string>()
    const [loading, setLoading] = useState(true)

    async function load() {
        setLoading(true)
        const res = await doFetch<GetUsersResponse>("/api/client/admin/users")
        if (res.data) {
            setUsers(res.data.users)
            setServers(res.data.servers)
            setError(undefined)
        } else {
            setError(res.errorString ?? "Failed to load admin data")
        }
        setLoading(false)
    }

    useEffect(() => {
        void load()
    }, [])

    return <div className="min-h-screen bg-neutral-900 text-neutral-100">
        <header className="sticky top-0 z-10 bg-neutral-800 border-b border-neutral-700">
            <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Link to="/" className="flex items-center gap-2">
                        <img src="/apple-touch-icon.png" className="rounded-md h-8" alt="Logo"/>
                    </Link>
                    <h1 className="text-xl font-semibold">Admin Dashboard</h1>
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-300">
                    {props.avatarUrl && <img src={props.avatarUrl} className="size-7 rounded-full" alt=""/>}
                    <span>{props.userName}</span>
                </div>
            </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-6 space-y-8">
            {error && <div className="bg-red-900/50 border border-red-700 text-red-200 rounded-md p-3">{error}</div>}

            <section className="space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                        Users {users && <span className="text-neutral-400 font-normal">({users.length})</span>}
                    </h2>
                    <button onClick={() => void load()}
                            className="text-sm bg-neutral-800 hover:bg-neutral-700 rounded-md px-3 py-1.5 border border-neutral-700">
                        Refresh
                    </button>
                </div>
                <UsersTable users={users} loading={loading} onTierChanged={(id, tier) =>
                    setUsers(prev => prev?.map(u => u.id === id ? {...u, subscriptionTier: tier} : u))}/>
            </section>

            <section className="space-y-3">
                <h2 className="text-lg font-semibold">
                    Servers {servers && <span className="text-neutral-400 font-normal">({servers.length})</span>}
                </h2>
                <ServersTable servers={servers} users={users} loading={loading}/>
            </section>

            <PaddleDangerZone/>
        </main>
    </div>
}

// Deletes all stored Paddle customer/subscription data. Needed once when switching Paddle from test mode
// to production, where the old test-mode entities no longer exist.
function PaddleDangerZone() {
    const [busy, setBusy] = useState(false)
    const [message, setMessage] = useState<string>()
    const [isError, setIsError] = useState(false)

    async function purge() {
        const confirmed = window.confirm(
            "Delete ALL stored Paddle customer and subscription data?\n\n" +
            "Use this once when switching Paddle from test mode to production. This cannot be undone.\n" +
            "Note: users keep their current subscription tier until a new webhook (or a manual override) updates it."
        )
        if (!confirmed) return

        setBusy(true)
        setMessage(undefined)
        const res = await doFetch<{ deletedCustomers: number, deletedSubscriptions: number }>(
            "/api/client/admin/paddle/purge", {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({confirm: true}),
            })
        setBusy(false)
        if (res.ok && res.data) {
            setIsError(false)
            setMessage(`Deleted ${res.data.deletedCustomers} customer(s) and ${res.data.deletedSubscriptions} subscription(s).`)
        } else {
            setIsError(true)
            setMessage(res.errorString ?? "Failed to delete Paddle data")
        }
    }

    return <section className="space-y-3">
        <h2 className="text-lg font-semibold text-red-400">Danger Zone</h2>
        <div className="rounded-md border border-red-800/60 bg-red-950/30 p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="max-w-xl">
                <p className="font-medium">Delete all subscription &amp; customer data</p>
                <p className="text-sm text-neutral-400">
                    Removes every stored Paddle customer and subscription record. Use once when switching Paddle
                    from test mode to production. Does not change users' current tiers.
                </p>
            </div>
            <div className="flex items-center gap-3">
                {message && <span className={`text-sm ${isError ? "text-red-400" : "text-green-400"}`}>{message}</span>}
                <button onClick={() => void purge()} disabled={busy}
                        className="text-sm bg-red-700 hover:bg-red-600 rounded-md px-3 py-1.5 disabled:opacity-50 whitespace-nowrap">
                    {busy ? "Deleting…" : "Delete all Paddle data"}
                </button>
            </div>
        </div>
    </section>
}

function Forbidden() {
    return <div className="min-h-screen bg-neutral-900 text-neutral-100 flex items-center justify-center">
        <div className="text-center space-y-3">
            <p className="text-5xl font-bold text-neutral-600">403</p>
            <p className="text-neutral-300">You do not have access to this page.</p>
            <Link to="/" className="inline-block text-primary hover:underline">Go home</Link>
        </div>
    </div>
}

function LoadingScreen() {
    return <div className="fixed inset-0 bg-neutral-900 flex items-center justify-center">
        <div className="flex flex-col items-center">
            <SvgLoader/>
            <p className="mt-4 text-lg font-semibold tracking-wide">Loading...</p>
        </div>
    </div>
}
